import React, { useEffect, useRef, useState } from 'react';
import Loading from '../../../../Components/Loading';
import Text from '../../../../Components/Base/Text/Text';
import apiClient from '../../../../Services/ApiClient';
import useSocket from '../../Hooks/useSocket';
import { Socket } from '../../Enums/Socket.enum';
import { JobStatus } from '../../Enums/JobStatus.enum';
import styles from './JobWaiting.module.scss';

interface Props {
	jobId: string;
	onJobSuccess: () => void;
	onJobFailed: () => void;
	onJobChained?: (chainedJobId: string) => void;
	timeout: number;
	pollInterval?: number;
	waitingMessage: string;
}

export interface JobCompleteSocketData {
	jobId: string;
	status: JobStatus;
	chainedJobId?: string;
	statusMessage?: string;
}

const JobWaiting: React.FC<Props> = ({
	jobId,
	waitingMessage,
	onJobSuccess,
	onJobFailed,
	onJobChained,
	timeout,
	pollInterval = 10000,
}) => {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const finishedRef = useRef(false);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);

	const clearWaitingTimeout = () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = null;
	};

	const finish = (callback: () => void) => {
		if (finishedRef.current) return;
		finishedRef.current = true;
		clearWaitingTimeout();
		callback();
	};

	const renewTimeout = () => {
		clearWaitingTimeout();
		timeoutRef.current = setTimeout(() => checkJobStatus(true), timeout);
	};

	const checkJobStatus = async (failWhenUnavailable: boolean) => {
		if (finishedRef.current) return;
		const { status } = await apiClient.head(`/job/${jobId}`);
		if (finishedRef.current) return;
		switch (status) {
			case 200:
				finish(onJobSuccess);
				break;
			case 202:
				renewTimeout();
				break;
			case undefined:
				if (failWhenUnavailable) finish(onJobFailed);
				break;
			default:
				finish(onJobFailed);
		}
	};

	useEffect(() => {
		finishedRef.current = false;
		renewTimeout();
		checkJobStatus(false);
		const interval = setInterval(() => checkJobStatus(false), pollInterval);
		return () => {
			finishedRef.current = true;
			clearWaitingTimeout();
			clearInterval(interval);
		};
	}, []);

	const handleOnSocket = (data: JobCompleteSocketData) => {
		if (data.jobId === jobId) {
			if (data.statusMessage) {
				setStatusMessage(data.statusMessage);
			}
			switch (data.status) {
				case JobStatus.SUCCESS:
					finish(onJobSuccess);
					break;
				case JobStatus.PENDING:
					renewTimeout();
					break;
				case JobStatus.CHAINED:
					if (data.chainedJobId) onJobChained?.(data.chainedJobId);
					renewTimeout();
					break;
				case JobStatus.FAILED:
					finish(onJobFailed);
					break;
			}
		}
	};

	useSocket(Socket.JOB_COMPLETED, (data: JobCompleteSocketData) =>
		handleOnSocket(data),
	);

	return (
		<div className={styles.wrapper}>
			<Loading />
			<Text tx={statusMessage ?? waitingMessage} />
		</div>
	);
};

export default JobWaiting;
